import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import LoanFilePreviewSidebar from "./LoanFilePreviewSidebar";
import ExtractTrifoldModal from "./extracts/ExtractTrifoldModal";
import { canSeeLoanExtracts } from "./extracts/loanExtractsAccess";
import {
  useLoanExtractsIndex,
  findExtractForDoc,
} from "./extracts/useLoanExtractsIndex";
import { useAuth } from "../../../../auth/AuthProvider";
import {
  filePreviewClear,
  filePreviewSetData,
} from "../../../../redux/reducers/filePreviewReducer";

// Screen-level mount of the improved file-preview sidebar. Driven by the
// `filePreview` redux slice (`data` = the previewed loan document) so the
// preview PERSISTS across drilldown tab navigation — it lives above the tab
// content rather than inside the Documents tab's table — and stays PINNED to
// the right edge of the screen (LoanFilePreviewSidebar is a fixed,
// right-docked panel). Opening from anywhere is just
// `dispatch(filePreviewSetData(doc))`; closing is `dispatch(filePreviewClear())`.
//
// The "Parsed" trifold viewer is owned HERE (not inside the sidebar) so opening
// it can also close the sidebar without unmounting the trifold along with it.
const GlobalLoanFilePreviewSidebar = () => {
  const dispatch = useDispatch();
  const file = useSelector((s) => s.filePreview?.data);
  const loan = useSelector((s) => s.loanDrilldown);
  // When a right-docked drilldown drawer is open (e.g. the Activity log), dock
  // the preview on the LEFT so the two sit side by side instead of overlapping.
  const rightDrawerOpen = useSelector((s) => !!s.sidebar?.type);
  const { user } = useAuth();
  const showExtracts = canSeeLoanExtracts(user?.email);

  // Trifold viewer opened from the sidebar's "Parsed" badge (a
  // parent_document_id). Kept here so closing the sidebar doesn't unmount it.
  const [trifoldSelectedId, setTrifoldSelectedId] = useState(null);

  const loanId = loan?._id;

  // Extracts index — fetched while a file is being previewed OR the trifold is
  // open (so the trifold keeps its data after the sidebar closes). Gated to the
  // extracts allowlist.
  const { documents: extractedDocuments } = useLoanExtractsIndex(loanId, {
    enabled: showExtracts && (!!file || !!trifoldSelectedId),
  });
  const matchedExtract = useMemo(
    () => findExtractForDoc(extractedDocuments, file),
    [extractedDocuments, file],
  );

  // Close any open preview (and trifold) when navigating to a DIFFERENT loan so
  // a stale file from the previous loan doesn't linger in the now-persistent
  // sidebar.
  const prevLoanIdRef = useRef(loanId);
  useEffect(() => {
    if (prevLoanIdRef.current && prevLoanIdRef.current !== loanId) {
      dispatch(filePreviewClear());
      setTrifoldSelectedId(null);
    }
    prevLoanIdRef.current = loanId;
  }, [loanId, dispatch]);

  const handleClose = useCallback(
    () => dispatch(filePreviewClear()),
    [dispatch],
  );
  const handleSelect = useCallback(
    (f) => dispatch(filePreviewSetData(f)),
    [dispatch],
  );

  // Open the trifold AND close the sidebar preview.
  const handleOpenParsed = useCallback(() => {
    if (!matchedExtract) return;
    setTrifoldSelectedId(matchedExtract.parent_document_id);
    dispatch(filePreviewClear());
  }, [matchedExtract, dispatch]);

  return (
    <>
      <LoanFilePreviewSidebar
        file={file}
        open={!!file}
        loan={loan}
        anchor={rightDrawerOpen ? "left" : "right"}
        containerId={file?.containerName || file?.dealId}
        // Navigation prev/next spans the loan's documents (screen-level — the
        // table's filtered/sorted list isn't available here).
        files={loan?.loanDocuments || []}
        onClose={handleClose}
        onSelectFile={handleSelect}
        parsedExtract={matchedExtract}
        onOpenParsed={handleOpenParsed}
      />

      {/* Trifold viewer opened from the "Parsed" badge. */}
      <ExtractTrifoldModal
        open={!!trifoldSelectedId}
        dealId={loanId}
        documents={extractedDocuments}
        selectedId={trifoldSelectedId}
        onSelectId={setTrifoldSelectedId}
        onClose={() => setTrifoldSelectedId(null)}
      />
    </>
  );
};

export default GlobalLoanFilePreviewSidebar;
